import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Skeleton
} from '@mui/material';
import EstablishmentStorageUtils, { EstablishmentStorageData } from '../Storages/establishmentStorage';
import CompetenciaStorageUtils from '../Storages/competenciaStorage';
import { getToken as getAuthToken } from '../Storages/cookies';

interface EstablishmentItem {
  establishment_alias_id: number | string;
  establishment_id: number | string | null;
  description: string;
  cnpj?: string | null;
}

interface Props {
  onChangeEstablishment: (aliasId: number | string, establishmentId?: number | string | null) => void;
  /** Quando true, o componente renderiza apenas o conteúdo interno, sem o Card wrapper */
  disableCard?: boolean;
}

const EstablishmentSelector: React.FC<Props> = ({ onChangeEstablishment, disableCard = false }) => {
  const [loading, setLoading] = useState(true);
  const [establishmentSelecionado, setEstablishmentSelecionado] = useState<number | string>('');
  const [establishments, setEstablishments] = useState<EstablishmentItem[]>([]);
  const onChangeEstablishmentRef = useRef(onChangeEstablishment);

  useEffect(() => {
    onChangeEstablishmentRef.current = onChangeEstablishment;
  }, [onChangeEstablishment]);

  const fetchData = useCallback(async () => {
    try {
      const token = getAuthToken() ?? '';

      const baseUrl = process.env.REACT_APP_API_URL || '';
      const url = baseUrl.endsWith('/') ? `${baseUrl}establishments-by-user` : `${baseUrl}/establishments-by-user`;

      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        }
      });
      const json = await res.json();

        if (Array.isArray(json?.data)) {
          // Ordena alfabeticamente pelo campo description (ignora maiúsculas/minúsculas, mas mantém acentos)
          const orderedData = [...json.data].sort((a: EstablishmentItem, b: EstablishmentItem) =>
            a.description.localeCompare(b.description, 'pt-BR', { sensitivity: 'accent' })
          );

          // Apenas estabelecimentos com vínculo válido
          const validEstablishments = orderedData.filter(
            (item: EstablishmentItem) => item.establishment_id !== null && item.establishment_id !== undefined
          );

          setEstablishments(validEstablishments);

          if (validEstablishments.length === 0) {
            console.warn('[EstablishmentSelector] Nenhum estabelecimento com vínculo válido foi retornado para o usuário.');
            EstablishmentStorageUtils.clear();
            onChangeEstablishmentRef.current?.('', null);
            return;
          }

          let estabelecimentoParaSelecionar: EstablishmentItem | null = null;

          const salvo = EstablishmentStorageUtils.get();
          if (salvo?.establishment_alias_id && salvo?.establishment_id) {
            const found = validEstablishments.find(
              (e: EstablishmentItem) => e.establishment_alias_id == salvo.establishment_alias_id
            );
            if (found) {
              estabelecimentoParaSelecionar = found;
            }
          } else if (salvo) {
            // Remove registros antigos sem establishment_id para evitar inconsistências
            EstablishmentStorageUtils.clear();
          }

          if (!estabelecimentoParaSelecionar) {
            estabelecimentoParaSelecionar = validEstablishments[0];
          }

          if (estabelecimentoParaSelecionar) {
            setEstablishmentSelecionado(estabelecimentoParaSelecionar.establishment_alias_id);
            EstablishmentStorageUtils.save({
                establishment_alias_id: estabelecimentoParaSelecionar.establishment_alias_id,
                establishment_id: estabelecimentoParaSelecionar.establishment_id,
                description: estabelecimentoParaSelecionar.description,
                cnpj: estabelecimentoParaSelecionar.cnpj || null
              });
              onChangeEstablishmentRef.current?.(
                estabelecimentoParaSelecionar.establishment_alias_id,
                estabelecimentoParaSelecionar.establishment_id
              );
          }
        }
    } catch (error) {
      console.error('Erro ao buscar estabelecimentos', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleChange = (event: SelectChangeEvent<string | number>) => {
    const selected = event.target.value;
    const estabelecimentoSelecionado = establishments.find(
      e => e.establishment_alias_id == selected
    );
    setEstablishmentSelecionado(selected);

    // Limpa competências salvas sempre que estabelecimento mudar
    CompetenciaStorageUtils.clear();

    if (estabelecimentoSelecionado) {
      EstablishmentStorageUtils.save({
          establishment_alias_id: estabelecimentoSelecionado.establishment_alias_id,
          establishment_id: estabelecimentoSelecionado.establishment_id,
          description: estabelecimentoSelecionado.description,
          cnpj: estabelecimentoSelecionado.cnpj || null
        });
        onChangeEstablishmentRef.current?.(
          estabelecimentoSelecionado.establishment_alias_id,
          estabelecimentoSelecionado.establishment_id
        );

      // Recarrega a página inteira para que todos os componentes busquem dados do novo estabelecimento
      window.location.reload();
    }
  };

  if (loading) {
    const skeletonContent = (
      <Box sx={{ p: 1 }}>
        <Skeleton variant="text" width="120px" height={20} sx={{ mb: 1 }} />
        <Skeleton variant="rectangular" width="100%" height={40} sx={{ borderRadius: 1 }} />
      </Box>
    );

    return disableCard ? skeletonContent : (
      <Card>
        <CardContent>{skeletonContent}</CardContent>
      </Card>
    );
  }

    if (establishments.length === 0) return <></>;

  const selectContent = (
    <FormControl size='small' sx={{ minWidth: 480 }}>
      <InputLabel id='establishment-label'>Estabelecimento</InputLabel>
      <Select
        labelId='establishment-label'
        value={establishmentSelecionado}
        label='Estabelecimento'
        onChange={handleChange}
      >
        {establishments.map(({ establishment_alias_id, description }) => (
          <MenuItem key={establishment_alias_id} value={establishment_alias_id}>
            {description}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return disableCard ? selectContent : (
    <Card>
      <CardContent>{selectContent}</CardContent>
    </Card>
  );
};

export default EstablishmentSelector; 